import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { AlertTriangle, CalendarDays, UserMinus, UsersRound } from 'lucide-react'

import { dashboardApi } from '@/services/dashboard'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { getServiceInstanceIdFromSearch, withServiceInstanceId } from '@/lib/service-instance'
import { cn, formatDate } from '@/lib/utils'
import type { DashboardUpcomingWorkdayScheduleItem, WorkerDayAvailabilityItem, WorkerDayLeaveItem } from '@/types'

const SCHEDULING_TABS = [
  { value: '/job/realtime', label: 'Real Time' },
  { value: '/jobs/scheduling', label: 'Schedule' },
  { value: '/job/timeline', label: 'Timeline' },
  { value: '/workdays', label: 'Work Days' },
] as const

const toInputDate = (date: Date) => {
  const tzOffset = date.getTimezoneOffset()
  const local = new Date(date.getTime() - tzOffset * 60 * 1000)
  return local.toISOString().slice(0, 10)
}

const INPUT_DATE_RE = /^\d{4}-\d{2}-\d{2}$/

const isValidInputDate = (value: string | null) => {
  if (!value || !INPUT_DATE_RE.test(value)) {
    return false
  }
  const parsed = new Date(value)
  return !Number.isNaN(parsed.getTime())
}

export default function ScheduleWorkersPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search])

  const urlStart = searchParams.get('start')
  const urlEnd = searchParams.get('end')
  const urlView = searchParams.get('view')

  const today = useMemo(() => toInputDate(new Date()), [])
  const defaultEnd = useMemo(() => {
    const value = new Date()
    value.setDate(value.getDate() + 6)
    return toInputDate(value)
  }, [])

  const activeSchedulingTab = useMemo(() => {
    const pathname = location.pathname
    if (pathname.startsWith('/job/realtime')) {
      return '/job/realtime'
    }
    if (pathname.startsWith('/job/timeline') || pathname.startsWith('/timeline')) {
      return '/job/timeline'
    }
    if (pathname.startsWith('/workdays')) {
      return '/workdays'
    }
    return '/jobs/scheduling'
  }, [location.pathname])

  const handleSchedulingTabNavigate = (nextPath: string) => {
    if (!nextPath || nextPath === activeSchedulingTab) {
      return
    }

    navigate(withServiceInstanceId(nextPath, serviceInstanceId))
  }

  const [start, setStart] = useState(() => (isValidInputDate(urlStart) ? (urlStart as string) : today))
  const [end, setEnd] = useState(() => (isValidInputDate(urlEnd) ? (urlEnd as string) : defaultEnd))
  const [viewMode, setViewMode] = useState<'week' | 'day'>(() => (urlView === 'day' ? 'day' : 'week'))
  const [locationFilter, setLocationFilter] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const calendarDateRef = useRef<HTMLInputElement | null>(null)

  const shiftDate = (value: string, days: number) => {
    const parsed = new Date(value)
    if (Number.isNaN(parsed.getTime())) {
      return value
    }
    parsed.setDate(parsed.getDate() + days)
    return toInputDate(parsed)
  }

  const handleMoveDateRange = (days: number) => {
    setStart((prev) => shiftDate(prev, days))
    setEnd((prev) => shiftDate(prev, days))
  }

  const openCalendarPicker = () => {
    if (!calendarDateRef.current) {
      return
    }

    if (typeof calendarDateRef.current.showPicker === 'function') {
      calendarDateRef.current.showPicker()
      return
    }

    calendarDateRef.current.click()
  }

  const queryKey = useMemo(() => ['job-schedule-workers-overview', start, end], [start, end])
  const { data, isLoading, isError } = useQuery({
    queryKey,
    queryFn: () => dashboardApi.getScheduleWorkersOverview(start, end),
    placeholderData: keepPreviousData,
  })

  const schedules = data?.upcoming_workday_schedules || []
  const availabilityItems = data?.worker_day_availability || []
  const leaveItems = data?.worker_day_leaves || []

  const locationOptions = useMemo(
    () => {
      const values = schedules.map((entry) => {
        const raw = String(entry.location_name || '').trim()
        return raw || 'Unknown location'
      })
      return Array.from(new Set(values)).sort((a, b) => a.localeCompare(b))
    },
    [schedules],
  )

  const categoryOptions = useMemo(
    () => {
      const values = schedules.map((entry) => {
        const raw = String(entry.category_name || '').trim()
        return raw || 'Unknown category'
      })
      return Array.from(new Set(values)).sort((a, b) => a.localeCompare(b))
    },
    [schedules],
  )

  const filteredSchedules = useMemo(() => {
    const locationNeedle = locationFilter.trim().toLowerCase()
    const categoryNeedle = categoryFilter.trim().toLowerCase()

    return schedules.filter((entry) => {
      const locationValue = (String(entry.location_name || '').trim() || 'Unknown location').toLowerCase()
      const categoryValue = (String(entry.category_name || '').trim() || 'Unknown category').toLowerCase()

      const locationMatches = !locationNeedle || locationValue.includes(locationNeedle)
      const categoryMatches = !categoryNeedle || categoryValue.includes(categoryNeedle)

      return locationMatches && categoryMatches
    })
  }, [schedules, locationFilter, categoryFilter])

  const sevenDays = buildSevenDayWindow(start)
  const availabilityLookup = useMemo(
    () => buildWorkerAvailabilityLookup(availabilityItems),
    [availabilityItems],
  )
  const leaveLookup = useMemo(
    () => buildWorkerLeaveLookup(leaveItems),
    [leaveItems],
  )
  const workerNameLookup = useMemo(
    () => buildWorkerNameLookup(schedules, availabilityItems, leaveItems),
    [schedules, availabilityItems, leaveItems],
  )
  const weeklyRows = buildWeeklyRows(filteredSchedules, sevenDays, availabilityLookup, leaveLookup, workerNameLookup)
  const dayWorkerIds = useMemo(
    () => collectWorkerIdsForSingleDay(availabilityLookup, leaveLookup, start),
    [availabilityLookup, leaveLookup, start],
  )
  const workerCandidates = collectWorkerCandidates(weeklyRows)
  const dayEntries = useMemo(
    () => buildDayEntries(filteredSchedules, start, leaveLookup),
    [filteredSchedules, start, leaveLookup],
  )
  const availableDayKeys = useMemo(() => {
    const keys = filteredSchedules
      .map((entry) => resolveEntryDay(entry))
      .filter((value): value is string => Boolean(value))

    return Array.from(new Set(keys)).sort((a, b) => a.localeCompare(b))
  }, [filteredSchedules])
  const dayWorkerRows = useMemo(
    () => buildDayWorkerRows(dayEntries, dayWorkerIds, workerNameLookup),
    [dayEntries, dayWorkerIds, workerNameLookup],
  )
  const highlightAssignment = data?.unassigned_assignments?.[0]
  const outOfAvailabilityCount = weeklyRows.reduce(
    (count, row) => count + row.cells.filter((cell) => cell.isOutsideAvailability).length,
    0,
  )
  const hasOutOfAvailabilityAllocations = outOfAvailabilityCount > 0
  const dayClashCount = dayEntries.filter((entry) => entry.hasClash).length
  const dayLeaveCount = dayEntries.filter((entry) => entry.isOnLeave).length

  useEffect(() => {
    const nextStart = isValidInputDate(urlStart) ? (urlStart as string) : today
    const nextEnd = isValidInputDate(urlEnd) ? (urlEnd as string) : defaultEnd
    const nextView: 'week' | 'day' = urlView === 'day' ? 'day' : 'week'

    setStart((prev) => (prev === nextStart ? prev : nextStart))
    setEnd((prev) => (prev === nextEnd ? prev : nextEnd))
    setViewMode((prev) => (prev === nextView ? prev : nextView))
  }, [defaultEnd, today, urlEnd, urlStart, urlView])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    params.set('view', viewMode)
    params.set('start', start)
    params.set('end', end)

    const nextSearch = `?${params.toString()}`
    if (nextSearch === location.search) {
      return
    }

    navigate(
      {
        pathname: location.pathname,
        search: nextSearch,
      },
      { replace: true },
    )
  }, [end, location.pathname, location.search, navigate, start, viewMode])

  useEffect(() => {
    if (viewMode !== 'day') {
      return
    }
    if (dayEntries.length > 0) {
      return
    }
    if (availableDayKeys.length === 0) {
      return
    }
    if (availableDayKeys.includes(start)) {
      return
    }

    setStart(availableDayKeys[0])
  }, [availableDayKeys, dayEntries.length, start, viewMode])

  if (isLoading) {
    return (
      <div className="space-y-4 p-4 sm:p-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="h-24 w-full rounded-xl" />
          ))}
        </div>
      </div>
    )
  }

  if (isError || !data) {
    return (
      <div className="space-y-4 p-4 sm:p-6">
        <h1 className="text-2xl font-semibold">Schedule Workers</h1>
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground">
            Unable to load schedule workers snapshot.
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-4 bg-white p-4 sm:p-6">
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-[#dfeae3] bg-[#f7fbf8] p-2">
        {SCHEDULING_TABS.map((tab) => {
          const isActive = activeSchedulingTab === tab.value
          return (
            <Button
              key={tab.value}
              type="button"
              variant="ghost"
              onClick={() => handleSchedulingTabNavigate(tab.value)}
              className={cn(
                'h-8 rounded-md px-3 text-xs font-semibold',
                isActive
                  ? 'bg-[#169b62] text-white hover:bg-[#118753] hover:text-white'
                  : 'text-[#355445] hover:bg-[#e8f4ee]'
              )}
            >
              {tab.label}
            </Button>
          )
        })}
      </div>

      <div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <h1 className="text-[34px] font-semibold tracking-tight text-[#20352b]">Schedule Workers for Future Jobs</h1>
          <p className="text-[12px] text-[#718078]">
            Plan ahead, assign workers and avoid clashes.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <Button
            onClick={() => navigate(withServiceInstanceId('/job/create', serviceInstanceId))}
            className="h-9 rounded-md bg-[#169b62] px-4 text-xs font-semibold hover:bg-[#118753]"
          >
            + New Job
          </Button>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
        <MetricTile icon={<CalendarDays className="h-4 w-4" />} tone="blue" value={data.summary.total_workday_schedules_count} label="Jobs This Week" />
        <MetricTile icon={<UsersRound className="h-4 w-4" />} tone="green" value={data.summary.assigned_workday_schedules_count} label="Workers Assigned" />
        <MetricTile icon={<UserMinus className="h-4 w-4" />} tone="rose" value={data.summary.unassigned_assignments_count} label="Unassigned" />
        <MetricTile icon={<AlertTriangle className="h-4 w-4" />} tone="amber" value={data.summary.attention_work_day_entries_count ?? 0} label="Supervisor Attention" />
        <MetricTile icon={<AlertTriangle className="h-4 w-4" />} tone="amber" value={data.summary.clashed_schedules_count} label="Conflicts" />
      </div>

      <div className="grid gap-2 lg:grid-cols-4">
        <Input type="date" value={start} onChange={(event) => setStart(event.target.value)} className="h-9 border-[#dfeae3] text-xs" />
        <Input
          type="search"
          list="schedule-location-options"
          value={locationFilter}
          onChange={(event) => setLocationFilter(event.target.value)}
          placeholder="All Locations"
          className="h-9 border-[#dfeae3] text-xs"
        />
        <datalist id="schedule-location-options">
          {locationOptions.map((option) => (
            <option key={option} value={option} />
          ))}
        </datalist>
        <Input
          type="search"
          list="schedule-category-options"
          value={categoryFilter}
          onChange={(event) => setCategoryFilter(event.target.value)}
          placeholder="All Categories"
          className="h-9 border-[#dfeae3] text-xs"
        />
        <datalist id="schedule-category-options">
          {categoryOptions.map((option) => (
            <option key={option} value={option} />
          ))}
        </datalist>
        <Input type="date" value={end} onChange={(event) => setEnd(event.target.value)} className="h-9 border-[#dfeae3] text-xs" />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#dfeae3] bg-[#f7fbf8] px-3 py-2">
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-[#567064]">Selected Date</span>
          <Badge className="bg-[#ddf6e7] text-[#087a49] hover:bg-[#ddf6e7]">{formatDate(start)}</Badge>
        </div>
        <Tabs value={viewMode} onValueChange={(value) => setViewMode(value as 'week' | 'day')}>
          <TabsList className="grid h-8 w-[130px] grid-cols-2">
            <TabsTrigger value="day" className="text-xs">Day</TabsTrigger>
            <TabsTrigger value="week" className="text-xs">Week</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <Card className="border-[#dfeae3] shadow-none">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <CardTitle className="text-base text-[#20352b]">
                {viewMode === 'day' ? formatDate(start) : `${formatDate(data.period.start)} - ${formatDate(data.period.end)}`}
              </CardTitle>
              <Button
                type="button"
                variant="outline"
                className="h-7 border-[#dfeae3] px-2 text-[11px]"
                onClick={() => handleMoveDateRange(-1)}
              >
                Previous
              </Button>
              <Button
                type="button"
                variant="outline"
                className="h-7 border-[#dfeae3] px-2 text-[11px]"
                onClick={() => handleMoveDateRange(1)}
              >
                Next
              </Button>
              <Button
                type="button"
                variant="outline"
                className="h-7 border-[#dfeae3] px-2"
                onClick={openCalendarPicker}
                title="Pick date"
              >
                <CalendarDays className="h-3.5 w-3.5" />
              </Button>
              <Input
                ref={calendarDateRef}
                type="date"
                value={start}
                onChange={(event) => {
                  const nextStart = event.target.value
                  if (!nextStart) {
                    return
                  }
                  const current = new Date(start)
                  const next = new Date(nextStart)
                  if (Number.isNaN(current.getTime()) || Number.isNaN(next.getTime())) {
                    setStart(nextStart)
                    return
                  }
                  const diffMs = next.getTime() - current.getTime()
                  const diffDays = Math.round(diffMs / (24 * 60 * 60 * 1000))
                  setStart(nextStart)
                  setEnd((prev) => shiftDate(prev, diffDays))
                }}
                className="sr-only"
                aria-hidden="true"
                tabIndex={-1}
              />
            </div>
            <CardDescription className="text-xs text-[#718078]">
              {viewMode === 'day' ? `${dayEntries.length} jobs mapped against workers for the selected day` : `${data.summary.total_workday_schedules_count} scheduled entries`}
            </CardDescription>
          </div>
          <div className="flex flex-col items-end gap-2">
            <Tabs value={viewMode} onValueChange={(value) => setViewMode(value as 'week' | 'day')}>
              <TabsList className="grid h-8 w-[130px] grid-cols-2">
                <TabsTrigger value="day" className="text-xs">Day</TabsTrigger>
                <TabsTrigger value="week" className="text-xs">Week</TabsTrigger>
              </TabsList>
            </Tabs>
            <Badge className="bg-[#ddf6e7] text-[#087a49] hover:bg-[#ddf6e7]">{viewMode === 'day' ? 'Workers vs Jobs (00:00 - 23:59)' : 'Timeline'}</Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {outOfAvailabilityCount > 0 ? (
            <div className="rounded-md border border-[#f4c8cc] bg-[#fde9eb] px-3 py-2 text-xs text-[#a63c46]">
              <p className="font-semibold">Alert: {outOfAvailabilityCount} allocation{outOfAvailabilityCount > 1 ? 's are' : ' is'} outside worker availability.</p>
            </div>
          ) : null}
          {viewMode === 'day' && dayClashCount > 0 ? (
            <div className="rounded-md border border-[#8f1f2b] bg-[#cb3d4d] px-3 py-2 text-xs text-white">
              <p className="font-semibold">Clash Alert: {dayClashCount} job{dayClashCount > 1 ? 's have' : ' has'} a schedule conflict on this day.</p>
            </div>
          ) : null}
          {viewMode === 'day' && dayLeaveCount > 0 ? (
            <div className="rounded-md border border-[#f4c8cc] bg-[#fde9eb] px-3 py-2 text-xs text-[#a63c46]">
              <p className="font-semibold">Leave Alert: {dayLeaveCount} job{dayLeaveCount > 1 ? 's are' : ' is'} assigned to workers on leave.</p>
            </div>
          ) : null}
          {viewMode === 'week' ? (
            <div className="overflow-x-auto pb-1">
              <div className="min-w-[980px]">
                <div className="mb-2 grid grid-cols-12 gap-2 rounded-md bg-[#f6f8f7] p-2">
                  <div className="col-span-2 text-[11px] font-semibold text-[#4d5f56]">Worker</div>
                  {sevenDays.map((day) => (
                    <div key={day} className="col-span-1 text-[11px] font-semibold text-[#4d5f56]">
                      {formatCompactDayLabel(day)}
                    </div>
                  ))}
                  <div className="col-span-3 text-[11px] font-semibold text-[#4d5f56]">Notes</div>
                </div>

                {weeklyRows.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No schedules in this window.</p>
                ) : (
                  weeklyRows.map((row) => (
                    <div key={row.key} className="mb-2 grid grid-cols-12 gap-2 rounded-lg border border-[#eef3ef] p-2">
                      <div className="col-span-2 rounded-md p-1">
                        {row.workerId ? (
                          <Link to={`/workers/${row.workerId}`} className="text-sm font-semibold text-[#20352b] underline-offset-2 hover:underline">
                            {row.worker}
                          </Link>
                        ) : (
                          <p className="text-sm font-semibold text-[#20352b]">{row.worker}</p>
                        )}
                        <p className="text-[11px] text-[#718078]">{row.role}</p>
                      </div>
                      {row.cells.map((cell) => (
                        cell.isAvailable ? (
                          <div
                            key={cell.key}
                            className={`col-span-1 rounded-md px-2 py-2 ${cell.tone}`}
                          >
                            <p className={`text-[10px] font-medium ${cell.availabilityLabel === 'Unavailable' ? 'text-[#b7515a]' : 'text-[#087a49]'}`}>{cell.availabilityLabel}</p>
                            <p className={`mt-1 text-[9px] ${cell.availabilityLabel === 'Unavailable' ? 'text-[#b7515a]' : 'text-[#087a49]'}`}>{cell.availabilityText}</p>
                          </div>
                        ) : (
                          <div key={cell.key} className="col-span-1 space-y-1">
                            <div className={`rounded-md px-2 py-2 ${cell.tone}`}>
                              <p className="text-[9px] font-medium text-white">{cell.time}</p>
                              {cell.jobId ? (
                                <Link
                                  to={`/jobs/${cell.jobId}`}
                                  className="mt-1 line-clamp-2 text-[10px] text-white underline-offset-2 hover:underline"
                                >
                                  {cell.title}
                                </Link>
                              ) : (
                                <p className="mt-1 line-clamp-2 text-[10px] text-white">{cell.title}</p>
                              )}
                            </div>
                            <p className={`text-[9px] ${cell.isOutsideAvailability ? 'font-semibold text-[#b7515a]' : 'text-[#087a49]'}`}>
                              Avail: {cell.availabilityText}
                            </p>
                            {cell.isOutsideAvailability ? (
                              <p className="text-[9px] font-semibold text-[#b7515a]">{cell.isOnLeave ? 'ALERT: Worker on leave' : 'ALERT: Outside availability'}</p>
                            ) : null}
                          </div>
                        )
                      ))}
                      <div className="col-span-3 rounded-md bg-[#f8fbf9] px-2 py-2 text-[10px] text-[#6c7d74]">
                        {row.notes}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          ) : (
            <div className="rounded-md border border-[#eef3ef]">
              <div className="pb-1">
                <div className="grid grid-cols-[220px_1fr] border-b border-[#eef3ef] bg-[#f6f8f7]">
                  <div className="px-3 py-2 text-[11px] font-semibold text-[#4d5f56]">Workers</div>
                  <div className="px-2 py-2">
                    <p className="mb-1 text-[10px] font-medium text-[#5d6f66]">Jobs Timeline (3h grid)</p>
                    <div className="grid" style={{ gridTemplateColumns: `repeat(${DAY_TIMELINE_GRID_COLUMNS}, minmax(0, 1fr))` }}>
                      {Array.from({ length: DAY_TIMELINE_GRID_COLUMNS }).map((_, block) => {
                        const hour = block * DAY_TIMELINE_GRID_HOURS
                        return (
                          <div key={hour} className="border-l border-[#e7ece9] px-1 text-[10px] font-semibold text-[#4d5f56] first:border-l-0">
                            {String(hour).padStart(2, '0')}:00
                          </div>
                        )
                      })}
                    </div>
                  </div>
                </div>

                <div className="max-h-[620px] overflow-y-auto">
                    {dayWorkerRows.length === 0 ? (
                      <div className="px-3 py-4">
                        <p className="text-sm text-muted-foreground">No schedules on the selected day.</p>
                        {availableDayKeys.length > 0 ? (
                          <button
                            type="button"
                            className="mt-2 text-xs font-semibold text-[#169b62] underline-offset-2 hover:underline"
                            onClick={() => setStart(availableDayKeys[0])}
                          >
                            Jump to next scheduled day ({availableDayKeys[0]})
                          </button>
                        ) : null}
                      </div>
                    ) : (
                      dayWorkerRows.map((row) => {
                        const availabilityText = resolveWorkerDayAvailability(availabilityLookup, row.workerId, start)
                        const availabilityRanges = parseAvailabilityRanges(availabilityText)

                        return (
                        <div key={row.key} className="grid grid-cols-[220px_1fr] border-b border-[#f2f5f3] last:border-b-0">
                          <div className="px-3 py-2">
                            {row.workerId ? (
                              <Link to={`/workers/${row.workerId}`} className="text-sm font-semibold text-[#20352b] underline-offset-2 hover:underline">
                                {row.worker}
                              </Link>
                            ) : (
                              <p className="text-sm font-semibold text-[#20352b]">{row.worker}</p>
                            )}
                            <p className="text-[11px] text-[#718078]">{row.entries.length} assignment{row.entries.length !== 1 ? 's' : ''}</p>
                            {row.entries.some((entry) => entry.hasClash) ? (
                              <p className="text-[10px] font-semibold text-[#b7515a]">Clash detected</p>
                            ) : null}
                            {row.entries.some((entry) => entry.isOnLeave) ? (
                              <p className="text-[10px] font-semibold text-[#b7515a]">Worker on leave</p>
                            ) : null}
                          </div>

                          <div className="px-2 py-2">
                            <div
                              className="relative rounded-md border border-[#e7ece9] bg-[#fbfcfb]"
                              style={{
                                height: `${Math.max(32, row.laneCount * DAY_TIMELINE_LANE_HEIGHT + 8)}px`,
                              }}
                            >
                                {availabilityRanges.map(([availableStart, availableEnd], rangeIndex) => {
                                  const left = `${(availableStart / DAY_TIMELINE_TOTAL_MINUTES) * 100}%`
                                  const width = `${((availableEnd - availableStart) / DAY_TIMELINE_TOTAL_MINUTES) * 100}%`

                                  return (
                                    <div
                                      key={`${row.worker}-availability-${rangeIndex}`}
                                      className="absolute inset-y-0 rounded-sm bg-[#ddf6e7]"
                                      style={{ left, width }}
                                      title={`Available ${formatTimelineMinuteRange(availableStart, availableEnd)}`}
                                    />
                                  )
                                })}
                                <div className="absolute inset-0 grid" style={{ gridTemplateColumns: `repeat(${DAY_TIMELINE_GRID_COLUMNS}, minmax(0, 1fr))` }}>
                                  {Array.from({ length: DAY_TIMELINE_GRID_COLUMNS }).map((_, block) => (
                                    <div key={block} className="border-l border-[#edf2ee] first:border-l-0" />
                                ))}
                              </div>

                              {row.entries.map((entry, index) => {
                                const left = `${(entry.startMinute / DAY_TIMELINE_TOTAL_MINUTES) * 100}%`
                                const width = `${(Math.max(15, entry.endMinute - entry.startMinute) / DAY_TIMELINE_TOTAL_MINUTES) * 100}%`
                                const top = entry.laneIndex * DAY_TIMELINE_LANE_HEIGHT + 4
                                const tone = entry.hasClash
                                  ? 'bg-[#cb3d4d] text-white ring-1 ring-[#8f1f2b]'
                                  : entry.isOnLeave
                                    ? 'bg-[#c45a4f] text-white ring-1 ring-[#8f1f2b]'
                                  : (index % 2 === 0
                                    ? 'bg-[#2f8fdd] text-white'
                                    : 'bg-[#2d77bb] text-white')

                                const blockClassName = cn('absolute rounded px-2 py-1 shadow-sm', tone, entry.jobId ? 'cursor-pointer hover:brightness-95' : '')
                                const blockTitle = `${entry.title} (${entry.timeLabel})${entry.hasClash ? ' - Clash detected' : ''}${entry.isOnLeave ? ' - Worker on leave' : ''}`
                                const blockContent = (
                                  <>
                                    <div className="truncate text-[10px] font-semibold">{entry.title}</div>
                                    <div className="truncate text-[9px] opacity-90">{entry.timeLabel}</div>
                                    {entry.hasClash ? (
                                      <div className="text-[9px] font-semibold opacity-95">Clash</div>
                                    ) : entry.isOnLeave ? (
                                      <div className="text-[9px] font-semibold opacity-95">Leave</div>
                                    ) : null}
                                  </>
                                )

                                if (entry.jobId) {
                                  return (
                                    <Link
                                      key={`${row.worker}-${entry.id}`}
                                      to={withServiceInstanceId(`/jobs/${entry.jobId}`, serviceInstanceId)}
                                      className={blockClassName}
                                      style={{ left, width, top, minHeight: '22px' }}
                                      title={blockTitle}
                                    >
                                      {blockContent}
                                    </Link>
                                  )
                                }

                                return (
                                  <div
                                    key={`${row.worker}-${entry.id}`}
                                    className={blockClassName}
                                    style={{ left, width, top, minHeight: '22px' }}
                                    title={blockTitle}
                                  >
                                    {blockContent}
                                  </div>
                                )
                              })}
                            </div>
                          </div>
                        </div>
                        )
                      })
                    )}
                </div>
              </div>
            </div>
          )}

        </CardContent>
      </Card>

      <div className="grid gap-3 xl:grid-cols-3">
        <Card className="border-[#dfeae3] shadow-none">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm text-[#20352b]">Job Details</CardTitle>
            <span className="text-xs font-medium text-[#169b62]">Edit</span>
          </CardHeader>
          <CardContent className="space-y-1">
            <p className="text-base font-semibold text-[#20352b]">{highlightAssignment?.job_title || 'Office Cleaning'}</p>
            <p className="text-xs text-[#718078]">{highlightAssignment?.role_label || 'Standard'}</p>
            <p className="pt-2 text-xs text-[#718078]">
              {highlightAssignment?.scheduled_start ? formatDate(highlightAssignment.scheduled_start, { hour: '2-digit', minute: '2-digit' }) : 'Schedule not set'}
            </p>
          </CardContent>
        </Card>

        <Card className="border-[#dfeae3] shadow-none">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm text-[#20352b]">Assign Worker</CardTitle>
            <span className="text-xs text-[#3f7bc0]">Best match</span>
          </CardHeader>
          <CardContent className="space-y-1">
            {workerCandidates.length === 0 ? (
              <p className="text-sm text-muted-foreground">No worker suggestions available.</p>
            ) : (
              workerCandidates.map((candidate) => (
                <div key={`${candidate.name}-${candidate.workerId ?? 'no-id'}`} className="flex items-center justify-between py-1 text-xs">
                  {candidate.workerId ? (
                    <Link to={`/workers/${candidate.workerId}`} className="text-[#20352b] underline-offset-2 hover:underline">
                      ○ {candidate.name}
                    </Link>
                  ) : (
                    <span className="text-[#20352b]">○ {candidate.name}</span>
                  )}
                  <span className={candidate.hasOutsideAvailability ? 'text-[#b7515a] font-semibold' : 'text-[#169b62]'}>
                    {candidate.hasLeave ? 'On leave' : (candidate.hasOutsideAvailability ? 'Outside availability' : 'Available')}
                  </span>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <Card className="border-[#dfeae3] shadow-none">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm text-[#20352b]">Supervisor Attention</CardTitle>
            <span className="text-xs font-semibold text-[#b7515a]">{data.summary.attention_work_day_entries_count ?? 0}</span>
          </CardHeader>
          <CardContent className="space-y-1">
            {(data.attention_work_day_entries?.length ?? 0) === 0 ? (
              <p className="text-sm text-muted-foreground">No attention entries in this window.</p>
            ) : (
              (data.attention_work_day_entries ?? []).slice(0, 5).map((entry) => (
                <Link
                  key={entry.id}
                  to={`/workdays/${entry.id}/edit`}
                  className="block rounded-md border border-[#f4c8cc] bg-[#fde9eb] px-2 py-1 text-xs text-[#8f1f2b] underline-offset-2 hover:underline"
                >
                  <p className="truncate font-semibold">{entry.job_title || 'Untitled job'}</p>
                  <p className="truncate text-[11px]">{entry.worker_name || 'Unknown worker'}{entry.work_date ? ` · ${entry.work_date}` : ''}</p>
                </Link>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      <div className="flex items-center justify-between">
        <Button variant="outline" className="border-[#dfeae3] text-xs">Cancel</Button>
        {hasOutOfAvailabilityAllocations ? (
          <Button disabled className="cursor-not-allowed bg-[#9fa8a3] text-xs text-white hover:bg-[#9fa8a3]">
            Schedule Job (Blocked)
          </Button>
        ) : (
          <Button asChild className="bg-[#169b62] text-xs hover:bg-[#118753]"><Link to="/assignments">Schedule Job</Link></Button>
        )}
      </div>
    </div>
  )
}

type DayTimelineEntry = {
  id: string
  jobId: string | null
  title: string
  workerLabel: string
  workerId: string | null
  locationLabel: string | null
  startMinute: number
  endMinute: number
  timeLabel: string
  hasClash: boolean
  isOnLeave: boolean
}

type DayTimelineWorkerRow = {
  key: string
  worker: string
  workerId: string | null
  laneCount: number
  entries: Array<DayTimelineEntry & { laneIndex: number }>
}

const DAY_TIMELINE_TOTAL_MINUTES = 24 * 60
const DAY_TIMELINE_LANE_HEIGHT = 26
const DAY_TIMELINE_GRID_HOURS = 3
const DAY_TIMELINE_GRID_COLUMNS = 24 / DAY_TIMELINE_GRID_HOURS

function buildDayEntries(
  entries: DashboardUpcomingWorkdayScheduleItem[],
  selectedDate: string,
  leaveLookup: Set<string>,
): DayTimelineEntry[] {
  return entries
    .map((entry) => {
      const startDate = toTimelineDate(entry.planned_start_local || entry.planned_start)
      const endDate = toTimelineDate(entry.planned_end_local || entry.planned_end)
      const dayKey = resolveEntryDay(entry)

      if (dayKey !== selectedDate) {
        return null
      }

      const safeStartMinute = startDate ? (startDate.getHours() * 60 + startDate.getMinutes()) : 0
      const rawEndMinute = endDate ? (endDate.getHours() * 60 + endDate.getMinutes()) : (safeStartMinute + 60)
      const safeEndMinute = Math.max(safeStartMinute + 15, rawEndMinute)

      return {
        id: String(entry.id),
        jobId: entry.job_id == null ? null : String(entry.job_id),
        title: String(entry.job_title || 'Untitled job'),
        workerLabel: getWorkerDisplayName(entry),
        workerId: entry.worker_id == null ? null : String(entry.worker_id),
        locationLabel: entry.location_name ? String(entry.location_name) : null,
        startMinute: Math.max(0, Math.min(DAY_TIMELINE_TOTAL_MINUTES - 1, safeStartMinute)),
        endMinute: Math.max(1, Math.min(DAY_TIMELINE_TOTAL_MINUTES, safeEndMinute)),
        timeLabel: formatTimelineTimeWindow(startDate, endDate),
        hasClash: entry.status === 'clashed',
        isOnLeave: resolveWorkerOnLeave(leaveLookup, entry.worker_id ?? null, selectedDate),
      }
    })
    .filter((entry): entry is DayTimelineEntry => Boolean(entry))
    .sort((left, right) => left.startMinute - right.startMinute)
}

function buildDayWorkerRows(
  entries: DayTimelineEntry[],
  workerIds: string[] = [],
  workerNameLookup: Map<string, string> = new Map(),
): DayTimelineWorkerRow[] {
  const byWorker = new Map<string, DayTimelineEntry[]>()
  const workerMeta = new Map<string, { worker: string; workerId: string | null }>()

  for (const entry of entries) {
    const workerId = entry.workerId ? String(entry.workerId) : null
    const workerKey = workerId ? `id:${workerId}` : `name:${entry.workerLabel}`
    const group = byWorker.get(workerKey) ?? []
    group.push(entry)
    byWorker.set(workerKey, group)
    workerMeta.set(workerKey, {
      worker: entry.workerLabel,
      workerId,
    })
  }

  for (const workerId of workerIds) {
    const workerKey = `id:${workerId}`
    if (byWorker.has(workerKey)) {
      continue
    }

    byWorker.set(workerKey, [])
    workerMeta.set(workerKey, {
      worker: workerNameLookup.get(workerId) || `Worker #${workerId}`,
      workerId,
    })
  }

  return Array.from(byWorker.entries())
    .sort(([leftKey], [rightKey]) => {
      const leftMeta = workerMeta.get(leftKey)
      const rightMeta = workerMeta.get(rightKey)
      const leftWorker = leftMeta?.worker || leftKey
      const rightWorker = rightMeta?.worker || rightKey
      return leftWorker.localeCompare(rightWorker)
    })
    .map(([workerKey, workerEntries]) => {
      const meta = workerMeta.get(workerKey)
      const sorted = [...workerEntries].sort((left, right) => left.startMinute - right.startMinute)
      const laneEnds: number[] = []

      const withLanes = sorted.map((entry) => {
        let laneIndex = laneEnds.findIndex((endMinute) => entry.startMinute >= endMinute)
        if (laneIndex === -1) {
          laneIndex = laneEnds.length
          laneEnds.push(entry.endMinute)
        } else {
          laneEnds[laneIndex] = entry.endMinute
        }

        return {
          ...entry,
          laneIndex,
        }
      })

      return {
        key: workerKey,
        worker: meta?.worker || withLanes[0]?.workerLabel || 'Unknown worker',
        workerId: meta?.workerId ?? withLanes[0]?.workerId ?? null,
        laneCount: Math.max(1, laneEnds.length),
        entries: withLanes,
      }
    })
}

function toTimelineDate(value?: string | null) {
  if (!value) {
    return null
  }

  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

function formatTimelineTimeWindow(startDate: Date | null, endDate: Date | null) {
  if (!startDate && !endDate) {
    return '00:00 - 23:59'
  }

  const startLabel = startDate
    ? `${String(startDate.getHours()).padStart(2, '0')}:${String(startDate.getMinutes()).padStart(2, '0')}`
    : '00:00'
  const endLabel = endDate
    ? `${String(endDate.getHours()).padStart(2, '0')}:${String(endDate.getMinutes()).padStart(2, '0')}`
    : '23:59'

  return `${startLabel} - ${endLabel}`
}

function MetricTile({
  icon,
  tone,
  value,
  label,
}: {
  icon: ReactNode
  tone: 'blue' | 'green' | 'rose' | 'amber'
  value: number
  label: string
}) {
  const toneClass = {
    blue: 'bg-[#e7f3fd] text-[#2d77bb]',
    green: 'bg-[#ddf6e7] text-[#148653]',
    rose: 'bg-[#fde9eb] text-[#b7515a]',
    amber: 'bg-[#fff2de] text-[#ae7a2c]',
  }

  return (
    <Card className="border-[#dfeae3] shadow-none">
      <CardContent className="flex items-center gap-3 p-3">
        <div className={`flex h-8 w-8 items-center justify-center rounded-md ${toneClass[tone]}`}>
          {icon}
        </div>
        <div>
          <p className="text-[26px] leading-none text-[#20352b]">{value}</p>
          <p className="mt-1 text-[11px] text-[#718078]">{label}</p>
        </div>
      </CardContent>
    </Card>
  )
}

function buildWeeklyRows(
  entries: DashboardUpcomingWorkdayScheduleItem[],
  sevenDays: string[],
  availabilityLookup: Map<string, string>,
  leaveLookup: Set<string>,
  workerNameLookup: Map<string, string>,
) {
  const byWorker = new Map<string, {
    key: string
    worker: string
    workerId: string | number | null
    rows: DashboardUpcomingWorkdayScheduleItem[]
  }>()
  const workerNameById = new Map<string, string>()

  entries.forEach((entry) => {
    const workerId = entry.worker_id ?? null
    const worker = getWorkerDisplayName(entry).replace('Unassigned worker', 'Unassigned')
    const key = workerId != null ? `id:${String(workerId)}` : `name:${worker}`
    if (workerId != null && worker && worker !== 'Unassigned') {
      workerNameById.set(String(workerId), worker)
    }
    const group = byWorker.get(key)

    if (group) {
      group.rows.push(entry)
      return
    }

    byWorker.set(key, {
      key,
      worker,
      workerId,
      rows: [entry],
    })
  })

  const workerIdsInWindow = collectWorkerIdsForDays(availabilityLookup, leaveLookup, sevenDays)
  workerIdsInWindow.forEach((workerId) => {
    const key = `id:${workerId}`
    if (byWorker.has(key)) {
      return
    }

    byWorker.set(key, {
      key,
      worker: workerNameById.get(workerId) || workerNameLookup.get(workerId) || `Worker #${workerId}`,
      workerId,
      rows: [],
    })
  })

  return Array.from(byWorker.values()).map(({ key, worker, workerId, rows }) => {
    const scheduled = sevenDays.map((day, dayIndex) => {
      const availabilityText = resolveWorkerDayAvailability(availabilityLookup, workerId, day)
      const isOnLeave = resolveWorkerOnLeave(leaveLookup, workerId, day)
      const items = rows.filter((entry) => resolveEntryDay(entry) === day)
      if (items.length === 0) {
        const isUnavailable = availabilityText === 'Unavailable' || isOnLeave
        return {
          key: `${worker}-${day}-available`,
          time: '',
          title: '',
          availabilityLabel: isOnLeave ? 'On Leave' : (isUnavailable ? 'Unavailable' : 'Available'),
          availabilityText: isOnLeave ? 'On Leave' : availabilityText,
          isAvailable: true,
          isOutsideAvailability: false,
          isOnLeave,
          tone: isUnavailable ? 'bg-[#fde9eb]' : 'bg-[#ddf6e7]',
        }
      }

      const primary = pickDisplayEntry(items)
      const time = formatEntryTimeRange(primary)
      const isOutsideAvailability = isOnLeave || isScheduleOutsideAvailabilityWindow(time, availabilityText)
      const extraLabel = items.length > 1 ? ` +${items.length - 1}` : ''
      return {
        key: `${worker}-${day}-${dayIndex}`,
        jobId: primary.job_id,
        time,
        title: `${primary.job_title || 'Scheduled work'}${extraLabel}`,
        availabilityLabel: '',
        availabilityText: isOnLeave ? 'On Leave' : availabilityText,
        isAvailable: false,
        isOutsideAvailability,
        isOnLeave,
        tone: isOutsideAvailability ? 'bg-[#cb3d4d]' : resolveScheduleTone(primary),
      }
    })

    return {
      key,
      worker,
      workerId,
      role: 'Field Worker',
      cells: scheduled,
      notes: rows.length === 0
        ? 'No assignments yet'
        : (rows.some((entry) => entry.status === 'clashed') ? 'Potential clash detected' : 'Ready for scheduling'),
    }
  })
}

function collectWorkerIdsForDays(
  availabilityLookup: Map<string, string>,
  leaveLookup: Set<string>,
  days: string[],
) {
  const daySet = new Set(days)
  const workerIds = new Set<string>()

  availabilityLookup.forEach((_value, key) => {
    const [workerId, day] = key.split('|')
    if (!workerId || !daySet.has(day)) {
      return
    }
    workerIds.add(workerId)
  })

  leaveLookup.forEach((key) => {
    const [workerId, day] = key.split('|')
    if (!workerId || !daySet.has(day)) {
      return
    }
    workerIds.add(workerId)
  })

  return Array.from(workerIds).sort((a, b) => a.localeCompare(b))
}

function collectWorkerIdsForSingleDay(
  availabilityLookup: Map<string, string>,
  leaveLookup: Set<string>,
  day: string,
) {
  if (!day) {
    return []
  }

  return collectWorkerIdsForDays(availabilityLookup, leaveLookup, [day])
}

function buildWorkerNameLookup(
  schedules: DashboardUpcomingWorkdayScheduleItem[],
  availabilityItems: WorkerDayAvailabilityItem[],
  leaveItems: WorkerDayLeaveItem[],
) {
  const lookup = new Map<string, string>()

  const pushName = (workerId: number | string | null | undefined, workerName: string | null | undefined) => {
    if (workerId == null) {
      return
    }

    const id = String(workerId).trim()
    const name = String(workerName || '').trim()
    if (!id || !name) {
      return
    }

    lookup.set(id, name)
  }

  schedules.forEach((entry) => {
    pushName(entry.worker_id ?? null, entry.worker_name ?? null)
  })

  availabilityItems.forEach((entry) => {
    pushName(entry.worker_id ?? null, entry.worker_name ?? null)
  })

  leaveItems.forEach((entry) => {
    pushName(entry.worker_id ?? null, entry.worker_name ?? null)
  })

  return lookup
}

function buildWorkerAvailabilityLookup(items: WorkerDayAvailabilityItem[]) {
  const lookup = new Map<string, string>()

  items.forEach((item) => {
    const workerId = item.worker_id == null ? '' : String(item.worker_id).trim()
    const workDate = String(item.work_date || '').trim()
    if (!workerId || !workDate) {
      return
    }

    const key = `${workerId}|${workDate}`
    if (item.is_available === false) {
      lookup.set(key, '--:--')
      return
    }

    const from = String(item.from_time || '').trim()
    const to = String(item.to_time || '').trim()
    if (!from || !to) {
      if (!lookup.has(key)) {
        lookup.set(key, '--:--')
      }
      return
    }

    const nextValue = `${formatHourMinute(from)}-${formatHourMinute(to)}`
    const existing = lookup.get(key)
    if (!existing || existing === 'Available') {
      lookup.set(key, nextValue)
      return
    }
    if (existing !== 'Unavailable') {
      lookup.set(key, `${existing}, ${nextValue}`)
    }
  })

  return lookup
}

function buildWorkerLeaveLookup(items: WorkerDayLeaveItem[]) {
  const lookup = new Set<string>()

  items.forEach((item) => {
    const workerId = item.worker_id == null ? '' : String(item.worker_id).trim()
    const workDate = String(item.work_date || '').trim()
    if (!workerId || !workDate || item.is_on_leave !== true) {
      return
    }

    lookup.add(`${workerId}|${workDate}`)
  })

  return lookup
}

function resolveWorkerOnLeave(lookup: Set<string>, workerId: number | string | null, day: string) {
  if (workerId == null) {
    return false
  }
  const key = `${String(workerId).trim()}|${day}`
  return lookup.has(key)
}

function isScheduleOutsideAvailabilityWindow(scheduleTime: string, availabilityText: string) {
  const scheduleRange = parseHourMinuteRange(scheduleTime)
  if (!scheduleRange) {
    return false
  }

  const availabilityRaw = String(availabilityText || '').trim()
  if (!availabilityRaw || availabilityRaw === '--:--') {
    return false
  }

  if (availabilityRaw.toLowerCase() === 'unavailable') {
    return true
  }

  const availabilityRanges = availabilityRaw
    .split(',')
    .map((chunk) => parseHourMinuteRange(chunk.trim()))
    .filter((range): range is [number, number] => range != null)

  if (availabilityRanges.length === 0) {
    return false
  }

  return !availabilityRanges.some(([availableStart, availableEnd]) => {
    const [scheduledStart, scheduledEnd] = scheduleRange
    return scheduledStart >= availableStart && scheduledEnd <= availableEnd
  })
}

function parseHourMinuteRange(value: string): [number, number] | null {
  const raw = String(value || '').trim()
  const [startRaw, endRaw] = raw.split('-').map((part) => part.trim())
  if (!startRaw || !endRaw) {
    return null
  }

  const start = parseHourMinuteToMinutes(startRaw)
  const end = parseHourMinuteToMinutes(endRaw)
  if (start == null || end == null || end <= start) {
    return null
  }

  return [start, end]
}

function parseAvailabilityRanges(availabilityText: string): Array<[number, number]> {
  const raw = String(availabilityText || '').trim()
  if (!raw || raw === '--:--' || raw.toLowerCase() === 'unavailable' || raw.toLowerCase() === 'on leave') {
    return []
  }

  return raw
    .split(',')
    .map((chunk) => parseHourMinuteRange(chunk.trim()))
    .filter((range): range is [number, number] => range != null)
}

function formatTimelineMinuteRange(startMinute: number, endMinute: number) {
  const startHour = Math.floor(startMinute / 60)
  const startMin = startMinute % 60
  const endHour = Math.floor(endMinute / 60)
  const endMin = endMinute % 60
  return `${String(startHour).padStart(2, '0')}:${String(startMin).padStart(2, '0')} - ${String(endHour).padStart(2, '0')}:${String(endMin).padStart(2, '0')}`
}

function parseHourMinuteToMinutes(value: string): number | null {
  const match = String(value || '').trim().match(/^(\d{1,2}):(\d{2})$/)
  if (!match) {
    return null
  }

  const hours = Number(match[1])
  const minutes = Number(match[2])
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) {
    return null
  }
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return null
  }

  return hours * 60 + minutes
}

function formatHourMinute(value: string) {
  const raw = String(value || '').trim()
  const match = raw.match(/^(\d{1,2}):(\d{2})/)
  if (!match) {
    return raw
  }
  return `${match[1].padStart(2, '0')}:${match[2]}`
}

function resolveWorkerDayAvailability(lookup: Map<string, string>, workerId: number | string | null, day: string) {
  if (workerId == null) {
    return '--:--'
  }
  const key = `${String(workerId).trim()}|${day}`
  return lookup.get(key) || '--:--'
}

function buildSevenDayWindow(startDate: string) {
  const start = new Date(startDate)
  if (Number.isNaN(start.getTime())) {
    return []
  }

  const days: string[] = []
  for (let index = 0; index < 7; index += 1) {
    const day = new Date(start)
    day.setDate(start.getDate() + index)
    days.push(toInputDate(day))
  }
  return days
}

function resolveEntryDay(entry: DashboardUpcomingWorkdayScheduleItem) {
  const explicitDay = String(entry.work_date || '').trim()
  if (/^\d{4}-\d{2}-\d{2}$/.test(explicitDay)) {
    return explicitDay
  }

  // Fall back to planned_start UTC day when work_date is unavailable.
  const plannedStart = String(entry.planned_start || '').trim()
  if (!plannedStart) {
    return ''
  }

  if (/^\d{4}-\d{2}-\d{2}/.test(plannedStart)) {
    return plannedStart.slice(0, 10)
  }

  const parsed = new Date(plannedStart)
  if (Number.isNaN(parsed.getTime())) {
    return ''
  }
  return parsed.toISOString().slice(0, 10)
}

function getWorkerDisplayName(entry: DashboardUpcomingWorkdayScheduleItem) {
  const name = String(entry.worker_name || '').trim()
  if (name) {
    return name
  }

  if (entry.worker_id != null && String(entry.worker_id).trim()) {
    return `Worker #${String(entry.worker_id).trim()}`
  }

  return 'Unassigned worker'
}

function formatEntryTimeRange(entry: DashboardUpcomingWorkdayScheduleItem) {
  const normalized = formatRangeFromDateTimes(entry.planned_start, entry.planned_end)
  if (normalized) {
    return normalized
  }

  const start = entry.planned_start_local || entry.planned_start
  const end = entry.planned_end_local || entry.planned_end
  return `${toShortTime(start)}-${toShortTime(end)}`
}

function toShortTime(value?: string | null) {
  const raw = String(value || '').trim()
  if (!raw) {
    return '--'
  }

  // Date-only values do not include a schedule time; avoid coercing to midnight.
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return '--'
  }

  const directMatch = raw.match(/\b(\d{2}:\d{2})/)
  if (directMatch?.[1]) {
    return directMatch[1]
  }

  const parsed = new Date(raw)
  if (Number.isNaN(parsed.getTime())) {
    return raw
  }

  return parsed.toISOString().slice(11, 16)
}

function pickDisplayEntry(items: DashboardUpcomingWorkdayScheduleItem[]) {
  const withNonMidnight = items.find((item) => {
    const label = formatEntryTimeRange(item)
    return label !== '00:00-00:00' && label !== '-----'
  })
  return withNonMidnight || items[0]
}

function formatRangeFromDateTimes(startValue?: string | null, endValue?: string | null) {
  const start = parseScheduleDate(startValue)
  const end = parseScheduleDate(endValue)
  if (!start || !end) {
    return null
  }

  const startMs = start.getTime()
  const endMs = end.getTime()
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) {
    return null
  }

  const formatter = new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  })

  return `${formatter.format(start)}-${formatter.format(end)}`
}

function parseScheduleDate(value?: string | null) {
  const raw = String(value || '').trim()
  if (!raw || /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return null
  }

  const parsed = new Date(raw)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

function collectWorkerCandidates(rows: ReturnType<typeof buildWeeklyRows>) {
  return rows
    .filter((row) => row.worker && row.worker !== 'Unassigned')
    .map((row) => ({
      name: row.worker,
      workerId: row.workerId,
      hasOutsideAvailability: row.cells.some((cell) => cell.isOutsideAvailability),
      hasLeave: row.cells.some((cell) => cell.isOnLeave),
    }))
    .slice(0, 4)
}

function formatCompactDayLabel(value: string) {
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return value
  }

  return parsed.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: '2-digit',
  }).replace(',', '')
}

function resolveScheduleTone(entry: DashboardUpcomingWorkdayScheduleItem) {
  const hasAssignedWorker = entry.worker_id != null || Boolean(String(entry.worker_name || '').trim())
  if (!hasAssignedWorker) {
    return 'bg-[#cb3d4d]'
  }

  if (entry.status === 'clashed') {
    return 'bg-[#cf8a1a]'
  }

  return 'bg-[#2f8fdd]'
}
